import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react'

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState('monthly')

  // Direct payment handlers - live Stripe payment links
  const handlePlanPurchase = (planId) => {
    switch (planId) {
      case 'pro_monthly':
        window.location.href = 'https://buy.stripe.com/8x228raLveY4dOsdGdaVa0B'
        break
      case 'pro_yearly':
        window.location.href = 'https://buy.stripe.com/00w3cv3j36ry11G45DaVa0C'
        break
      case 'agency_yearly':
        window.location.href = 'https://buy.stripe.com/cNi7sLcTD2bieSweKhaVa0D'
        break
      default:
        break
    }
  }

  const plans = [
    {
      id: 'pro_monthly',
      name: 'Professional',
      description: 'Built for operators and teams that want consistent AI-powered outbound and a cleaner sales process.',
      features: [
        'AI-Powered Lead Generation + Personalized Cold Outreach',
        'Visual Workflow Builder',
        'CRM & Pipeline',
        'Multi-Provider Fallback (Gmail/Outlook/Brevo)'
      ],
      buttonText: 'Get Started Now',
      priceText: '$47',
      subtitle: '/month',
      note: 'Monthly access',
      cycle: 'monthly'
    },
    {
      id: 'pro_yearly',
      name: 'Pro Yearly',
      description: 'Best for long-term growth with stronger savings, faster support, and deeper reporting.',
      features: [
        'Everything in Professional',
        '2 Months Free',
        'Priority Email Support',
        'Advanced Analytics'
      ],
      buttonText: 'Save $264 Today',
      priceText: '$300',
      subtitle: '/year',
      note: '',
      cycle: 'yearly',
      badge: '⭐ MOST POPULAR Best Value\n$300/year\n($25/mo) Save $264 + Priority Support'
    },
    {
      id: 'agency_yearly',
      name: 'Agency / White Label',
      description: 'Your founder-special tier for launching a branded SaaS offer with client management built in.',
      features: [
        'Everything in Pro',
        'Full White-Label Rights',
        'Custom Logo & Branding',
        'Custom Domain Support',
        'Multi-Tenant Client Management'
      ],
      buttonText: 'Launch Your Own SaaS',
      priceText: '$497',
      subtitle: '/year',
      note: 'Sell Market Genie under your own logo and domain to your clients',
      cycle: 'yearly',
      featured: true
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-500 via-emerald-600 to-slate-500">
      {/* Navigation */}
      <nav className="relative z-50 bg-black/35 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 border border-white/40 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Market Genie</span>
            </Link>
            
            <Link
              to="/"
              className="inline-flex items-center text-white hover:text-yellow-300 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="py-20 bg-black/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-600 mb-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.3)', filter: 'brightness(1.1) saturate(1.2)'}}>
              Choose the Right Growth Tier
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Three clear paths for operators, growing teams, and agencies ready to launch their own branded SaaS offer.
            </p>
          </div>

          <div className="mb-10 flex flex-col items-center gap-3">
            <div className="inline-flex rounded-full border border-white/15 bg-slate-950/50 p-1 shadow-xl backdrop-blur">
              {['monthly', 'yearly'].map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                    billingCycle === cycle
                      ? 'bg-white text-slate-900 shadow'
                      : 'text-gray-200 hover:text-white'
                  }`}
                >
                  {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-400">Agency / White Label is always billed yearly.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {plans.map((plan, index) => {
              const isAgency = plan.id === 'agency_yearly'
              const isActiveCycle = isAgency || plan.cycle === billingCycle

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className={`relative h-full rounded-3xl p-8 ${plan.id === 'pro_yearly' ? 'pt-16' : ''} backdrop-blur-xl shadow-2xl transition-all duration-300 ${
                    isAgency
                      ? 'border-2 border-[#38BEBA] bg-slate-950/80 ring-2 ring-[#38BEBA]/25'
                      : isActiveCycle
                        ? 'border border-white/30 bg-slate-950/72'
                        : 'border border-white/10 bg-slate-950/55'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-5 left-0 right-0">
                      <span className={`block w-full rounded-full py-2 text-xs font-bold tracking-wide whitespace-pre-line break-words text-center ${
                        isAgency
                          ? 'bg-[#38BEBA] text-slate-950'
                          : 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950'
                      }`}>
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    {plan.note && <p className="text-sm text-teal-200 mb-3">{plan.note}</p>}
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                    </div>
                    <p className="text-sm text-gray-300 mb-5">{plan.description}</p>
                    {isAgency && (
                      <p className="text-sm text-teal-200 mb-5">Includes full partner dashboard, revenue tracking, and resale tools.</p>
                    )}
                    <div className="flex items-end gap-2 mb-1">
                      <span className="text-5xl font-bold text-white">{plan.priceText}</span>
                      <span className="pb-1 text-gray-300">{plan.subtitle}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-gray-200">
                        <CheckCircle className={`mt-0.5 mr-3 h-5 w-5 flex-shrink-0 ${isAgency ? 'text-teal-300' : 'text-green-400'}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanPurchase(plan.id)}
                    className={`mt-auto w-full rounded-xl px-6 py-3 font-semibold transition-all duration-200 transform hover:scale-[1.02] ${
                      isAgency
                        ? 'bg-[#38BEBA] text-slate-950 hover:bg-[#2fa7a3]'
                        : plan.id === 'pro_yearly'
                          ? 'bg-white text-slate-900 hover:bg-slate-100'
                          : 'border border-white/20 bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {plan.buttonText}
                  </button>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}
